import { Injectable, TemplateRef, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { SelectedPICardData } from 'src/app/shared/interfaces/prod-plan-model';

@Injectable()
export class ProductionPlanningDataService {
  overlayRef!: OverlayRef;

  private selectedNodeCodeSubject = new BehaviorSubject<string | null>(null);
  selectedNodeCode$ = this.selectedNodeCodeSubject.asObservable();

  private _filterMaster = new ReplaySubject<any>(1);
  public filterMaster$ = this._filterMaster.asObservable();

  private selectedFiltersSubject = new BehaviorSubject<any>({
    period_selected: 'Current Week',
    start_date: '',
    end_date: '',
    region: [],
    product_category: [],
    node_type: [],
    risk_level: [],
    unfulfilled_orders: false,
    production_loss: false,
    overstocked_locations: false,
    supplier_delay: false,
  });
  selectedFilters$ = this.selectedFiltersSubject.asObservable();

  private _selectedPICardData = new BehaviorSubject<SelectedPICardData>({
    total_indent: { month_def: [], receiver_date: [] },
    urgent_orders_card: { month_def: [], receiver_date: [] },
    total_demand: { month_def: [], for_date: [] },
    back_orders: { month_def: [], receiver_date: [] }
  });
  public selectedPICardData = this._selectedPICardData.asObservable();

  constructor(private overlay: Overlay) { }

  emitNodeCode(nodeCode: string) {
    this.selectedNodeCodeSubject.next(nodeCode);
  }

  public updateFilterMaster(filters) {
    this._filterMaster.next(filters);
  }

  setSelectedFilters(filters: any) {
    this.selectedFiltersSubject.next(filters);
  }

  resetSelectedFilters() {
    this.selectedFiltersSubject.next({
      period_selected: '',
      start_date: '',
      end_date: '',
      region: [],
      product_category: [],
      node_types: [],
      risk_level: [],
      unfulfilled_orders: false,
      production_loss: false,
      overstocked_locations: false,
      supplier_delay: false,
    });
  }

  getSelectedFilters() {
    return this.selectedFiltersSubject.getValue();
  }

  overlayPositioning(triggerBtn: HTMLElement, popoverTemplate: TemplateRef<any>, vcr: ViewContainerRef) {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null!;
      return;
    }

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(triggerBtn)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 8,
        },
      ]);

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      positionStrategy,
    });

    const portal = new TemplatePortal(popoverTemplate, vcr);
    this.overlayRef.attach(portal);

    this.overlayRef.backdropClick().subscribe(() => {
      this.overlayRef?.dispose();
      this.overlayRef = null!;
    });
  }

  updateSelectedPICardData(
    cardKey: keyof SelectedPICardData,
    monthData: any[]
  ): void {
    const selectedMonthDefs = new Set<string>();
    const selectedReceiverDates = new Set<string>();

    const dateKey = cardKey === 'total_demand' ? 'for_date' : 'receiver_date';

    monthData.forEach(month => {
      if (month.isSelected && month.month_def) {
        selectedMonthDefs.add(month.month_def);
      }

      (month.weekly_data || []).forEach(week => {
        if (week.isSelected && week[dateKey]) {
          selectedReceiverDates.add(week[dateKey]);
        }
      });
    });

    // Reset everything before setting the new active card
    const resetData: SelectedPICardData = {
      total_indent: { month_def: [], receiver_date: [] },
      urgent_orders_card: { month_def: [], receiver_date: [] },
      total_demand: { month_def: [], for_date: [] },
      back_orders: { month_def: [], receiver_date: [] },
    };

    const updated: SelectedPICardData = {
      ...resetData,
      [cardKey]: {
        month_def: Array.from(selectedMonthDefs),
        [dateKey]: Array.from(selectedReceiverDates)
      }
    };

    this._selectedPICardData.next(updated);
  }

  closeOverlay() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null!;
    }
  }
}
